import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FinanceModuleStyles.css';
import { Receipt, Plus, Search, Edit, Trash2, X, Calendar, DollarSign, FileCheck, Printer } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

const ReceiptModule = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: '',
    receivedFrom: '',
    amount: '',
    paymentMethod: 'cash',
    description: '',
    status: 'active'
  });

  useEffect(() => { fetchReceipts(); }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data?.data || [];
      console.log('Receipts loaded:', data.length);
      setReceipts(data);
    } catch (error) { console.error('Error:', error); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert amount to number before sending
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount) || 0
      };
      
      console.log('Submitting receipt:', submitData);
      
      if (editingReceipt) {
        await axios.put(`${API_URL}/receipts/${editingReceipt._id}`, submitData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/receipts`, submitData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false); setEditingReceipt(null); resetForm(); fetchReceipts();
    } catch (error) { 
      console.error('Submit error:', error);
      alert('Failed to save receipt: ' + (error.response?.data?.message || error.message)); 
    }
    setLoading(false);
  };

  const handleEdit = (receipt) => {
    setEditingReceipt(receipt);
    setFormData({
      receiptNumber: receipt.receiptNumber || '',
      Date: receipt.receipt_date ? receipt.receipt_date.split('T')[0] : '',
      receivedFrom: receipt.receivedFrom || '',
      amount: receipt.amount ? receipt.amount.toString() : '',
      paymentMethod: receipt.paymentMethod || 'cash',
      description: receipt.description || '',
      status: receipt.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this receipt?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/receipts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchReceipts();
    } catch (error) { alert('Failed to delete'); }
  };

  const handlePrint = (receipt) => {
    const printWindow = window.open('', '_blank');
    const formatCurrency = (amount) => `KES ${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try { return new Date(dateString).toLocaleDateString('en-GB'); } catch (e) { return 'N/A'; }
    };
    
    const actualAmount = receipt.total || receipt.total_amount || receipt.amount || 0;
    const actualDate = receipt.receipt_date || receipt.receipt_date || receipt.date || receipt.createdAt || '';
    
    printWindow.document.write(`
      <html>
      <head>
        <title>Receipt ${receipt.receiptNumber}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 40px; color: #1e293b; }
          .receipt-container { max-width: 600px; margin: 0 auto; border: 2px solid #e2e8f0; border-radius: 12px; padding: 40px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #667eea; padding-bottom: 20px; }
          .logo { width: 80px; height: 80px; border-radius: 50%; margin-bottom: 10px; }
          .company-name { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0; }
          .company-tagline { font-size: 12px; color: #64748b; margin: 4px 0; }
          .receipt-title { font-size: 24px; font-weight: 800; color: #667eea; margin: 20px 0; letter-spacing: 4px; }
          .receipt-number { font-size: 14px; color: #64748b; font-weight: 600; }
          .info-section { margin: 20px 0; }
          .info-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
          .info-row:last-child { border-bottom: none; }
          .info-label { font-weight: 600; color: #475569; }
          .info-value { color: #334155; font-weight: 500; }
          .amount-box { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 10px; padding: 24px; margin: 20px 0; text-align: center; border: 2px solid #667eea; }
          .amount-label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
          .amount-value { font-size: 32px; font-weight: 800; color: #0f172a; margin: 8px 0; font-family: 'Courier New', monospace; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; }
          .footer p { margin: 4px 0; font-size: 12px; color: #64748b; }
          .stamp-area { width: 100px; height: 100px; border: 2px dashed #dc2626; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 20px auto; transform: rotate(-15deg); opacity: 0.6; }
          .stamp-area span { color: #dc2626; font-size: 10px; font-weight: 700; text-transform: uppercase; text-align: center; }
          @media print { body { padding: 0; } .receipt-container { border: none; } }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <img src="https://www.secs.gismac.co.ke/logos/gismac.jpg" class="logo" alt="GISMAC Security" />
            <h1 class="company-name">Gismac Security Limited</h1>
            <p class="company-tagline">Professional Security Solutions</p>
            <div class="receipt-title">RECEIPT</div>
            <div class="receipt-number">#${receipt.receiptNumber || 'N/A'}</div>
          </div>
          
          <div class="info-section">
            <div class="info-row"><span class="info-label">Date:</span><span class="info-value">${formatDate(actualDate)}</span></div>
            <div class="info-row"><span class="info-label">Received From:</span><span class="info-value">${receipt.receivedFrom || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">Payment Method:</span><span class="info-value">${receipt.paymentMethod || 'Cash'}</span></div>
            <div class="info-row"><span class="info-label">Description:</span><span class="info-value">${receipt.description || '-'}</span></div>
            <div class="info-row"><span class="info-label">Status:</span><span class="info-value">${receipt.status || 'Active'}</span></div>
          </div>
          
          <div class="amount-box">
            <div class="amount-label">Amount Received</div>
            <div class="amount-value">${formatCurrency(actualAmount)}</div>
          </div>
          
          <div class="stamp-area"><span>OFFICIAL<br>STAMP</span></div>
          
          <div class="footer">
            <p><strong>Thank you for your payment!</strong></p>
            <p>📧 accounts@gismac.co.ke | 🌐 www.secs.gismac.co.ke</p>
            <p>📍 Nairobi, Kenya</p>
            <p style="margin-top: 10px; font-size: 10px; color: #94a3b8;">Generated by Gismac Security Management System</p>
          </div>
        </div>
        <script>window.onload = () => { setTimeout(() => window.print(), 500); };</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const resetForm = () => setFormData({
    receiptNumber: '', date: '', receivedFrom: '', amount: '', paymentMethod: 'cash', description: '', status: 'active'
  });

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num) || amount === null || amount === undefined) return 'KES 0.00';
    return `KES ${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB');
    } catch (e) { return 'N/A'; }
  };

  const filteredReceipts = receipts.filter(r =>
    r.receiptNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.receivedFrom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAmount = receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return (
    <div className="finance-module">
      <div className="module-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src="/logos/gismac.jpg" alt="GISMAC" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '50%' }} />
          <div>
            <h2><Receipt size={24} /> Receipt Management</h2>
            <p className="module-subtitle">Track and manage all payment receipts</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditingReceipt(null); resetForm(); setShowModal(true); }}>
          <Plus size={18} /> New Receipt
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-box blue">
          <div className="stat-icon"><DollarSign size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(totalAmount)}</span>
            <span className="stat-label">Total Received</span>
          </div>
        </div>
        <div className="stat-box teal">
          <div className="stat-icon"><Receipt size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{receipts.length}</span>
            <span className="stat-label">Total Receipts</span>
          </div>
        </div>
        <div className="stat-box slate">
          <div className="stat-icon"><FileCheck size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{receipts.filter(r => r.status === 'active').length}</span>
            <span className="stat-label">Active</span>
          </div>
        </div>
        <div className="stat-box indigo">
          <div className="stat-icon"><Calendar size={20} /></div>
          <div className="stat-info">
            <span className="stat-value">{new Date().toLocaleDateString('en-KE')}</span>
            <span className="stat-label">Today</span>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder="Search receipts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Receipt #</th>
              <th>Date</th>
              <th>Received From</th>
              <th>Amount</th>
              <th>Payment</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="text-center">Loading...</td></tr> :
             filteredReceipts.length === 0 ? <tr><td colSpan="8" className="text-center">No receipts found</td></tr> :
             filteredReceipts.map(receipt => (
              <tr key={receipt._id}>
                <td><span className="receipt-badge">{receipt.receiptNumber}</span></td>
                <td>{formatDate(receipt.receipt_date || receipt.date || receipt.createdAt)}</td>
                <td><strong>{receipt.receivedFrom}</strong></td>
                <td className="amount-cell">{formatCurrency(receipt.total || receipt.total_amount || receipt.amount)}</td>
                <td><span className="payment-badge">{receipt.paymentMethod}</span></td>
                <td>{receipt.description || '-'}</td>
                <td>
                  <span className={`status-pill status-${receipt.status}`}>
                    {receipt.status}
                  </span>
                </td>
                <td className="actions">
                  <button className="btn-icon" onClick={() => handlePrint(receipt)} title="Print"><Printer size={14} /></button>
                  <button className="btn-icon" onClick={() => handleEdit(receipt)} title="Edit"><Edit size={14} /></button>
                  <button className="btn-icon" onClick={() => handleDelete(receipt._id)} title="Delete"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingReceipt ? 'Edit' : 'New'} Receipt</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="module-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Receipt Number *</label>
                  <input type="text" value={formData.receiptNumber} onChange={e => setFormData({...formData, receiptNumber: e.target.value})} placeholder="e.g. RCP-001" required />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Received From *</label>
                  <input type="text" value={formData.receivedFrom} onChange={e => setFormData({...formData, receivedFrom: e.target.value})} placeholder="Payer name" required />
                </div>
                <div className="form-group">
                  <label>Amount (KES) *</label>
                  <input type="number" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} placeholder="Enter amount" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={formData.paymentMethod} onChange={e => setFormData({...formData, paymentMethod: e.target.value})}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mpesa">M-Pesa</option>
                    <option value="cheque">Cheque</option>
                    <option value="card">Card</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="2" placeholder="Receipt description..." />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingReceipt ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptModule;
